import { FileTextIcon, PictureInPictureIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PinnedDocument } from '@/services/slices/pinnedDocsSlice';

interface DockTabProps {
  doc: PinnedDocument;
  isActive: boolean;
  onClick: () => void;
  onClose: (e: React.MouseEvent) => void;
}

// Truncate filename to max length
const truncateFilename = (name: string, maxLength = 18): string => {
  if (name.length <= maxLength) return name;
  const ext = name.lastIndexOf('.');
  if (ext > 0 && name.length - ext <= 5) {
    // Keep extension if short
    const baseName = name.slice(0, ext);
    const extension = name.slice(ext);
    const truncatedBase = baseName.slice(0, maxLength - 3 - extension.length);
    return `${truncatedBase}...${extension}`;
  }
  return `${name.slice(0, maxLength - 3)}...`;
};

// Truncate matter name
const truncateMatterName = (name: string, maxLength = 10): string => {
  if (name.length <= maxLength) return name;
  return `${name.slice(0, maxLength)}...`;
};

export const DockTab = ({ doc, isActive, onClick, onClose }: DockTabProps) => {
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose(e);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'group flex items-center gap-1.5 h-7 px-2 rounded-t-md transition-all duration-150',
            'text-xs font-medium whitespace-nowrap',
            'border-t border-l border-r',
            isActive
              ? 'bg-violet-600 text-white border-violet-600'
              : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700 hover:text-white',
          )}
        >
          <FileTextIcon className="h-3 w-3 shrink-0" />
          <span className="truncate max-w-[100px]">
            {truncateFilename(doc.fileName)}
          </span>
          <span
            className={cn(
              'text-[10px] opacity-60',
              isActive ? 'text-violet-200' : 'text-zinc-500',
            )}
          >
            • {truncateMatterName(doc.matterName)}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className={cn(
                  'h-4 w-4 p-0 ml-0.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity',
                  isActive
                    ? 'hover:bg-violet-500 text-violet-200 hover:text-white'
                    : 'hover:bg-zinc-600 text-zinc-500 hover:text-white',
                )}
              >
                <PictureInPictureIcon size={12} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Put it Back
            </TooltipContent>
          </Tooltip>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        <div className="font-medium">{doc.fileName}</div>
        <div className="text-muted-foreground">{doc.matterName}</div>
      </TooltipContent>
    </Tooltip>
  );
};

export default DockTab;
